import { Trans, t } from "@lingui/macro";
import React, { useCallback } from "react";
import cx from "classnames";
import format from "date-fns/format";
import Button from "components/Button/Button";
import { padIndex } from "pages/Event/utils/padIndex";
import { EmptyPlaceholder } from "components/EmptyPlaceholder";
import { useContributions } from "../hooks/blockchain/useContributions";
import { useClaimableRewards } from "../hooks/blockchain/useClaimableRewards";
import { formatDistanceToNowStrict, fromUnixTime, isBefore } from "date-fns";
import { useExpiredRewards } from "../hooks/blockchain/useExpiredRewards";
import { formatBigAmount } from "../utils/formatAmount";
import { importImage } from "lib/legacy";
import { BigNumber } from "ethers";

const palmIconWhite = importImage("PalmTokenWhite.svg");

type Props = React.ComponentProps<"div"> & {};

const TAB_IDS = {
  CONTRIBUTION: "CONTRIBUTION",
  CLAIMABLE_REWARDS: "CLAIMABLE_REWARDS",
  EXPIRED_REWARDS: "EXPIRED_REWARDS",
} as const;

type TabId = keyof typeof TAB_IDS;

type TabsData = {
  [key in TabId]: {
    label: string;
  };
};

const TableTh = (props: React.ComponentProps<"th">) => {
  return (
    <th
      className={cx([
        "first:rounded-l-sm last:rounded-r-sm bg-background-3 h-14 text-[1.2rem] leading-[1.5] text-inactive font-normal text-left px-6 first:left-0 first:sticky",
        props.className,
      ])}
    >
      {props.children}
    </th>
  );
};

const TableTd = (props: React.ComponentProps<"td">) => {
  return (
    <td
      className={cx([
        "text-[1.4rem] leading-[1.5] first:left-0 first:sticky first:bg-background-5-v2-only",
        props.className,
      ])}
    >
      <div className="px-6 h-[4.5rem] flex items-center gap-3">{props.children}</div>
    </td>
  );
};

const TableBodyTr = (props: React.ComponentProps<"tr">) => {
  return <tr className={cx(["border-b", props.className])}>{props.children}</tr>;
};

const formatDate = (date: Date | number): string => {
  return format(date, `MMM d, yyyy`);
};

const EventOverviewCard = (props: Props) => {
  const [selectedTabId, setSelectedTabId] = React.useState<TabId>("CONTRIBUTION");

  const tabsData = React.useMemo(() => {
    return {
      CONTRIBUTION: {
        label: t`Contribution`,
      },
      CLAIMABLE_REWARDS: {
        label: t`Claimable Rewards`,
      },
      EXPIRED_REWARDS: {
        label: t`Expired Rewards`,
      },
    } satisfies TabsData;
  }, []);

  const displayedTabIds = ["CONTRIBUTION", "CLAIMABLE_REWARDS", "EXPIRED_REWARDS"] satisfies TabId[];

  const contributions = useContributions();

  const { rewardItems: claimableRewards, claim, isClaiming } = useClaimableRewards();

  const expiredRewards = useExpiredRewards();

  const getTabCount = useCallback(
    (tabId: TabId): number => {
      switch (tabId) {
        case "CONTRIBUTION": {
          return contributions.length;
        }
        case "CLAIMABLE_REWARDS": {
          return claimableRewards.length;
        }
        case "EXPIRED_REWARDS": {
          return expiredRewards.length;
        }
        default: {
          throw new Error("Unsupported tabId");
        }
      }
    },
    [contributions.length, claimableRewards.length, expiredRewards.length]
  );

  const isTableEmpty = React.useMemo(() => {
    return getTabCount(selectedTabId) === 0;
  }, [selectedTabId, getTabCount]);

  return (
    <div
      className={cx([
        "rounded-[2.5rem] border bg-background-5-v2-only @container/card relative rounded-[2.5rem] p-[2.5rem] flex flex-col items-stretch",
        props.className,
      ])}
    >
      <div className="flex flex-col gap-2">
        <p className="text-[2.4rem] leading-[1.4] font-extrabold font-mazzard">
          <Trans>Rewards Overview</Trans>
        </p>

        <p className="text-inactive text-[1.2rem] leading-[1.5]">
          <Trans>Your Rewards Details</Trans>
        </p>
      </div>

      <div className="mt-10 min-h-0 flex flex-col items-stretch">
        <div>
          <ul className="flex gap-14 border-b whitespace-nowrap overflow-x-auto scroll-smooth hide-scrollbar">
            {displayedTabIds.map((tabId) => (
              <li key={tabId} className="relative">
                <button onClick={() => setSelectedTabId(tabId)} className="flex items-center gap-2 pb-[1.2rem]">
                  <div
                    className={cx([
                      "text-[1.4rem] leading-[1.5]",
                      {
                        "text-inactive hover:text-[#642afa]": tabId !== selectedTabId,
                        "text-transparent bg-gradient-to-bl from-[#632afa] to-[#7156d9] bg-clip-text":
                          tabId === selectedTabId,
                      },
                    ])}
                  >
                    {tabsData[tabId].label}
                  </div>

                  <div className="text-[1rem] text-inactive rounded-full min-w-[2rem] h-[2rem] bg-background-3 flex justify-center items-center px-1.5">
                    {getTabCount(tabId)}
                  </div>
                </button>

                {tabId === selectedTabId && (
                  <div className="absolute h-[.3rem] rounded-tl-full rounded-tr-full w-full left-0 right-0 bottom-0 bg-gradient-to-bl from-[#632afa] to-[#7156d9] pointer-events-none" />
                )}
              </li>
            ))}
          </ul>
        </div>

        <div className="mt-[1.5rem] relative min-h-0">
          <div className="h-full overflow-y-auto scroll-smooth isolate whitespace-nowrap hide-scrollbar">
            {selectedTabId === "CONTRIBUTION" && (
              <table className="w-full min-h-0">
                <thead className="sticky top-0 z-10">
                  <tr>
                    <TableTh>
                      <Trans>#</Trans>
                    </TableTh>

                    <TableTh>
                      <Trans>Tier</Trans>
                    </TableTh>

                    <TableTh>
                      <Trans>Total Commitment</Trans>
                    </TableTh>

                    <TableTh>
                      <Trans>Total PLP</Trans>
                    </TableTh>

                    <TableTh>
                      <Trans>Total Rewards</Trans>
                    </TableTh>

                    <TableTh>
                      <Trans>Status</Trans>
                    </TableTh>
                  </tr>
                </thead>
                <tbody className="overflow-y-auto">
                  {contributions.map((el, index) => (
                    <TableBodyTr key={index}>
                      <TableTd>{padIndex(el.index + 1, getTabCount(selectedTabId))}</TableTd>

                      <TableTd>
                        <Trans>Tier {el.tier + 1}</Trans>
                      </TableTd>

                      <TableTd>{`$${formatBigAmount(el.paidAmount, 18)}`}</TableTd>

                      <TableTd>{formatBigAmount(el.purchasedAmount, 18)}</TableTd>

                      <TableTd>{formatBigAmount(el.rewardAmount, 18)}</TableTd>

                      <TableTd className="">
                        <div
                          className={cx([
                            "h-10 px-6 inline-flex items-center gap-2 rounded-full",
                            "text-[1.2rem] leading-[2.4rem]",
                            {
                              "text-green bg-green/10": el.isActive,
                              "text-red bg-red/10": !el.isActive,
                            },
                          ])}
                        >
                          <div className="rounded-full w-[.6rem] aspect-square bg-current-color"></div>

                          <div>{el.isActive ? t`Active` : t`Inactive`}</div>
                        </div>
                      </TableTd>
                    </TableBodyTr>
                  ))}
                </tbody>
              </table>
            )}
            {selectedTabId === "CLAIMABLE_REWARDS" && (
              <table className="w-full min-h-0">
                <thead className="sticky top-0 z-10">
                  <tr>
                    <TableTh>
                      <Trans>#</Trans>
                    </TableTh>

                    <TableTh>
                      <Trans>Tier</Trans>
                    </TableTh>

                    <TableTh>
                      <Trans>Total Reward</Trans>
                    </TableTh>

                    <TableTh>
                      <Trans>Already Unlocked</Trans>
                    </TableTh>

                    <TableTh>
                      <Trans>Already Claimed</Trans>
                    </TableTh>

                    <TableTh>
                      <Trans>Fully Unlocked</Trans>
                    </TableTh>

                    <TableTh>
                      <Trans>Claim</Trans>
                    </TableTh>
                  </tr>
                </thead>
                <tbody>
                  {claimableRewards.map((el, index) => (
                    <TableBodyTr key={index}>
                      <TableTd>{padIndex(el.index + 1, getTabCount(selectedTabId))}</TableTd>

                      <TableTd>
                        <Trans>Tier {el.tier + 1}</Trans>
                      </TableTd>

                      <TableTd className="flex gap-5">
                        {formatBigAmount(el.rewardAmount, 18)}
                        <img src={palmIconWhite} alt="icon" className="h-7" />
                      </TableTd>

                      <TableTd>
                        {formatBigAmount(el.unlockedAmount, 18)} <img src={palmIconWhite} alt="icon" className="h-7" />
                      </TableTd>

                      <TableTd>{formatBigAmount(el.alreadyClaimed, 18)}</TableTd>

                      <TableTd>{el.fullyUnlockedAt ? formatDate(fromUnixTime(el.fullyUnlockedAt)) : "-"}</TableTd>

                      {el.isExpired ? (
                        <TableTd />
                      ) : (
                        <TableTd>
                          {el.nextUnlockAt && el.rewardAmount?.gt(el.alreadyClaimed || BigNumber.from(0)) ? (
                            isBefore(fromUnixTime(el.nextUnlockAt), new Date()) && el.claimableAmount?.gt(0) ? (
                              <Button
                                disabled={isClaiming[el.index]}
                                variant="light-green"
                                size="sm"
                                onClick={() => claim(el.index)}
                              >
                                <Trans>Claim {formatBigAmount(el.claimableAmount, 18)}</Trans>
                              </Button>
                            ) : (
                              <Button variant="minor" size="sm" disabled>
                                <Trans>In {formatDistanceToNowStrict(fromUnixTime(el.nextUnlockAt))}</Trans>
                              </Button>
                            )
                          ) : (
                            "-"
                          )}
                        </TableTd>
                      )}
                    </TableBodyTr>
                  ))}
                </tbody>
              </table>
            )}
            {selectedTabId === "EXPIRED_REWARDS" && (
              <table className="w-full min-h-0">
                <thead className="sticky top-0 z-10">
                  <tr>
                    <TableTh>
                      <Trans>#</Trans>
                    </TableTh>

                    <TableTh>
                      <Trans>Tier</Trans>
                    </TableTh>

                    <TableTh>
                      <Trans>Total Reward</Trans>
                    </TableTh>

                    <TableTh>
                      <Trans>Already Saved</Trans>
                    </TableTh>

                    <TableTh>
                      <Trans>Rewards Expired</Trans>
                    </TableTh>
                  </tr>
                </thead>
                <tbody className="overflow-y-auto">
                  {expiredRewards.map((el, index) => (
                    <TableBodyTr key={index}>
                      <TableTd>{padIndex(el.index + 1, getTabCount(selectedTabId))}</TableTd>

                      <TableTd>
                        <Trans>Tier {el.tier + 1}</Trans>
                      </TableTd>

                      <TableTd>{formatBigAmount(el.rewardAmount, 18)}</TableTd>

                      <TableTd>{formatBigAmount(el.claimedRewardAmount, 18)}</TableTd>

                      <TableTd>{formatBigAmount(el.expiredRewardAmount, 18)}</TableTd>
                    </TableBodyTr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {isTableEmpty && <EmptyPlaceholder message={t`You don't have any contribution.`} />}
      </div>
    </div>
  );
};

export default EventOverviewCard;
